import {useEffect, useState} from "react"
import {useNavigate} from "react-router-dom"
import {useAuth} from "../../../app/auth/auth"
import {useAppSelector} from "../../../app/hooks"
import {GemPayment} from "../../../app/store/store"

import "./GemPaymentModal.css"
import fetchWithRetry, {withLanguage} from "../../../util/request";
import {IconCircleCheck, IconGift, IconX} from "@tabler/icons-react"
import Currency, {formatCurrency} from "../currency/Currency"
import Button from "../../../component/button/Button";
import {totalForMonths} from "../item/modal/monthPricing";
import {useTranslation} from "react-i18next";

export default function GemPaymentModal(props: {
    payment: GemPayment
    currencies: { [key: string]: Currency }
    close: () => void
}) {
    const {payment, currencies, close} = props;
    const {t} = useTranslation();

    const {item, gift} = payment;
    const months = payment.months ?? 1;
    const gemTotal = totalForMonths(item, months, "GEMS");

    const auth = useAppSelector(useAuth)
    const [gemBalance, setGemBalance] = useState<number | undefined>(undefined)

    const [result, setResult] = useState<null | {
        ok: boolean,
        message?: string
    }>()
    const [sending, setSending] = useState(false);

    const navigate = useNavigate()

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.setProperty("overflow", null);
        }
    })

    useEffect(() => {
        return fetchWithRetry(async res => {
            if (res.status === 401) navigate("/login");
            if (!res.ok) return false;
            let body = await res.text().then(parseInt);
            setGemBalance(body);
            return true;
        }, process.env.REACT_APP_API_BASE + `/store/gem/balance`, {
            headers: {
                Authorization: auth.token ?? ""
            }
        });
    }, [auth, setGemBalance, navigate, currencies])

    if (!auth.user) {
        navigate("/login");
        return null;
    }

    if (result?.ok) {
        return <div className={"gem-payment-modal-container"}>
            <IconX className={"close"} onClick={close}/>
            <div className={`gem-payment-modal${gemBalance ? "" : " loading"}`}>
                <img src={item.asset} alt={""}/>
                {gift ? <p className={"gift"}><IconGift/> {t("gemPayment.giftFor", {name: gift.name})}</p> : null}
                <h3>{item.name}</h3>
                <div className={"success spacer"}>
                    <p><IconCircleCheck size={32}/>{t("gemPayment.purchasePaid")}</p>
                    <Button onClick={close}>{t("common.close")}</Button>
                </div>
            </div>
        </div>
    }

    return <div className={"gem-payment-modal-container"}>
        <IconX className={"close"} onClick={close}/>
        <div className={`gem-payment-modal${gemBalance ? "" : " loading"}`}>
            {
                gemBalance !== undefined ? <>
                    <img src={item.asset} alt={""}/>
                    {gift ? <p className={"gift"}><IconGift/> {t("gemPayment.giftFor", {name: gift.name})}</p> : null}
                    <h3>{item.name}</h3>
                    {months > 1 ?
                        <p className={"months"}>{t("common.months", {count: months})}</p> : null}
                    <p className={"price"}>{formatCurrency(currencies["GEMS"], gemTotal)}</p>
                    {gemBalance >= gemTotal ? <>
                            <div className={"spacer"}>
                                {
                                    result?.ok === false ? <p className={"error"}>{result.message}</p> :
                                        <p className={"new-balance"}>{t("gemPayment.balanceAfter")}
                                            <span>{
                                                formatCurrency(
                                                    currencies["GEMS"],
                                                    gemBalance - gemTotal
                                                )
                                            }</span>
                                        </p>
                                }
                                <Button type={"green"} loading={sending} onClick={() => {
                                    if (sending) return;
                                    setSending(true);
                                    fetch(process.env.REACT_APP_API_BASE + `/store/gem/purchase`, withLanguage({
                                        method: "POST",
                                        body: JSON.stringify({
                                            item: payment.item.id,
                                            category: payment.category,
                                            gift: payment.gift?.uuid,
                                            months: months
                                        }),
                                        headers: {
                                            "Content-Type": "application/json",
                                            "Authorization": auth.token ?? ""
                                        }
                                    })).then(res => {
                                        if (res.ok) {
                                            setResult({
                                                ok: true
                                            })
                                        } else {
                                            res.json().then(body => {
                                                setResult({
                                                    ok: false,
                                                    message: body.message
                                                })
                                            })
                                        }
                                        setSending(false);
                                    }).catch(() => setSending(false))
                                }}>{t("gemPayment.confirmPurchase")}</Button>
                            </div>
                        </> :
                        <div className={"spacer"}>
                            <p className={"status"}>{t("gemPayment.needMoreGems", {
                                amount: formatCurrency(
                                    currencies["GEMS"],
                                    -gemBalance
                                    + gemTotal
                                )
                            })}</p>
                            <Button onClick={close}>{t("common.close")}</Button>
                        </div>
                    }
                </> : <div className={"loader big"}/>
            }
        </div>
    </div>;
}